import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { membersAPI, blogAPI, issuesAPI, coreAPI } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import ScanHub from './ScanHub';
import './Admin.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    members: 0,
    activeMembers: 0,
    posts: 0,
    publishedPosts: 0,
    issues: 0,
    activeIssues: 0,
    messages: 0,
    unreadMessages: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [membersRes, postsRes, issuesRes, messagesRes] = await Promise.all([
        membersAPI.getAll().catch(() => ({ data: [] })),
        blogAPI.adminGetPosts().catch(() => ({ data: [] })),
        issuesAPI.adminGetAll().catch(() => ({ data: [] })),
        coreAPI.adminGetMessages().catch(() => ({ data: [] })),
      ]);

      const members = Array.isArray(membersRes.data) ? membersRes.data : membersRes.data?.results || [];
      const posts = Array.isArray(postsRes.data) ? postsRes.data : postsRes.data?.results || [];
      const issues = Array.isArray(issuesRes.data) ? issuesRes.data : issuesRes.data?.results || [];
      const messages = Array.isArray(messagesRes.data) ? messagesRes.data : messagesRes.data?.results || [];

      setStats({
        members: members.length,
        activeMembers: members.filter(m => m.is_active).length,
        posts: posts.length,
        publishedPosts: posts.filter(p => p.status === 'published').length,
        issues: issues.length,
        activeIssues: issues.filter(i => i.is_active).length,
        messages: messages.length,
        unreadMessages: messages.filter(m => !m.is_read).length,
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-dashboard">
      <h1>管理員儀表板</h1>
      <ScanHub />
      {loading && <LoadingPage message="載入統計資料中..." />}
      {!loading && (
      <>
      <div className="dashboard-stats">
        <Link to="/admin/members" className="stat-card stat-members">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>會員管理</h3>
            <div className="stat-number">{stats.members}</div>
            <div className="stat-detail">啟用中: {stats.activeMembers} · 含 QR 掃描</div>
          </div>
        </Link>

        <Link to="/admin/blog" className="stat-card stat-blog">
          <div className="stat-icon">📝</div>
          <div className="stat-content">
            <h3>文章管理</h3>
            <div className="stat-number">{stats.posts}</div>
            <div className="stat-detail">已發布: {stats.publishedPosts}</div>
          </div>
        </Link>

        <Link to="/admin/issues" className="stat-card stat-issues">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <h3>議題管理</h3>
            <div className="stat-number">{stats.issues}</div>
            <div className="stat-detail">進行中: {stats.activeIssues}</div>
          </div>
        </Link>

        <Link to="/admin/messages" className="stat-card stat-messages">
          <div className="stat-icon">💬</div>
          <div className="stat-content">
            <h3>聯絡訊息</h3>
            <div className="stat-number">{stats.messages}</div>
            <div className="stat-detail">
              未讀: <span className="highlight">{stats.unreadMessages}</span>
            </div>
          </div>
        </Link>
      </div>

      <div className="dashboard-actions">
        <h2>快速操作</h2>
        <div className="quick-actions">
          <Link to="/admin/blog/new" className="btn btn-primary">新增文章</Link>
          <Link to="/admin/issues/new" className="btn btn-primary">新增核心服務</Link>
          <Link to="/admin/settings" className="btn btn-secondary">網站設定</Link>
          <Link to="/admin/organization-members" className="btn btn-secondary">組織架構</Link>
          <Link to="/admin/messages" className="btn btn-secondary">查看訊息</Link>
        </div>
      </div>
      </>
      )}

      <style jsx>{`
        .admin-dashboard {
          padding: 2rem;
        }

        .admin-dashboard h1 {
          margin-bottom: 2rem;
          color: #2c3e50;
        }

        .dashboard-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-bottom: 3rem;
        }

        .stat-card {
          display: flex;
          align-items: center;
          padding: 1.5rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          text-decoration: none;
          color: inherit;
          transition: all 0.3s ease;
          border-left: 4px solid #3498db;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        }

        .stat-card.stat-members { border-left-color: #3498db; }
        .stat-card.stat-events { border-left-color: #9b59b6; }
        .stat-card.stat-blog { border-left-color: #e74c3c; }
        .stat-card.stat-issues { border-left-color: #f39c12; }
        .stat-card.stat-messages { border-left-color: #1abc9c; }
        .stat-card.stat-scanner { border-left-color: #34495e; }

        .stat-icon {
          font-size: 3rem;
          margin-right: 1.5rem;
        }

        .stat-content h3 {
          margin: 0 0 0.5rem 0;
          font-size: 1.1rem;
          color: #7f8c8d;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: bold;
          color: #2c3e50;
          line-height: 1;
        }

        .stat-detail {
          margin-top: 0.5rem;
          font-size: 0.9rem;
          color: #95a5a6;
        }

        .stat-desc {
          font-size: 0.95rem;
          color: #7f8c8d;
        }

        .highlight {
          color: #e74c3c;
          font-weight: bold;
        }

        .dashboard-actions {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .dashboard-actions h2 {
          margin-bottom: 1.5rem;
          color: #2c3e50;
          font-size: 1.5rem;
        }

        .quick-actions {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .quick-actions .btn {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        @media (max-width: 768px) {
          .admin-dashboard {
            padding: 1rem;
          }

          .dashboard-stats {
            grid-template-columns: 1fr;
          }

          .stat-icon {
            font-size: 2rem;
            margin-right: 1rem;
          }

          .stat-number {
            font-size: 2rem;
          }

          .quick-actions {
            flex-direction: column;
          }

          .quick-actions .btn {
            width: 100%;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
